import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef, memo, useMemo, useCallback, CSSProperties } from 'react';
import { FixedSizeList, FixedSizeGrid } from 'react-window';
import {
  useDraggable,
} from '@dnd-kit/core';
import { addBadPoster, isBadPoster } from '../utils/badPosterCache';

// Safe storage wrapper to handle mobile storage issues
const safeCacheStorage = {
  getItem: (key: string) => {
    try {
      // Try localStorage first
      const localValue = localStorage.getItem(key);
      if (localValue) {
        return localValue;
      }
      
      // Fall back to sessionStorage if localStorage fails or is empty
      try {
        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue) {
          // console.log(`[Storage] Retrieved ${key} from sessionStorage fallback`);
          return sessionValue;
        }
      } catch (sessionError) {
        console.error(`[Storage Error] SessionStorage fallback failed:`, sessionError);
      }
      
      return null;
    } catch (e) {
      console.error(`[Storage Error] Failed to get ${key}:`, e);
      
      // Last attempt: try sessionStorage directly
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    }
  },
  
  setItem: (key: string, value: string) => {
    let localSuccess = false;
    let sessionSuccess = false;
    
    // Try localStorage first
    try {
      localStorage.setItem(key, value);
      localSuccess = true;
    } catch (e) {
      console.error(`[Storage Error] localStorage failed for ${key}:`, e);
    }
    
    // Always try to save to sessionStorage as well for redundancy
    try {
      sessionStorage.setItem(key, value);
      sessionSuccess = true;
    } catch (sessionError) {
      console.error(`[Storage Error] sessionStorage failed:`, sessionError);
    }
    
    return localSuccess || sessionSuccess;
  }
};

// Define card dimensions based on size prop
export const CARD_DIMENSIONS: Record<CardSize, { width: number; height: number; gap: number }> = {
  small: { width: 72, height: 121, gap: 8 }, // Reduced gap for smaller cards
  medium: { width: 90, height: 162, gap: 12 }, // Adjusted height based on PosterDraggable
  large: { width: 108, height: 203, gap: 16 }, // Adjusted height based on PosterDraggable
};

type CardSize = 'small' | 'medium' | 'large';

interface ImdbSearchProps {
  searchResults: any[];
  setSearchResults: (items: any[]) => void;
  tiers: any[];
  activeDrag: any;
  setActiveDrag: (info: { item: any; tierIdx?: number; itemIdx?: number }) => void;
  forceShowAll?: boolean; // Kept for potential future use, but virtualization handles showing all
  cardSize?: CardSize;
  hideSearchInput?: boolean;
  isTapInterface?: boolean;
  onTapItem?: (item: any, position: { x: number, y: number }) => void;
  useHorizontalLayout?: boolean;
  isLoadingExternally?: boolean; // Added prop
  listTitle?: string; // Added prop for IMDB list title
  isReadOnly?: boolean; // Add isReadOnly prop
}

const OMDB_API_KEY = 'ca946d84'; // Not used in proxy mode

function getCachedResults(query: string) {
  const cache = safeCacheStorage.getItem('searchResultsCache');
  if (!cache) return null;
  try {
    const parsed = JSON.parse(cache);
    return parsed[query] || null;
  } catch {
    return null;
  }
}

function setCachedResults(query: string, results: any[]) {
  let cache: Record<string, any[]> = {};
  try {
    cache = JSON.parse(safeCacheStorage.getItem('searchResultsCache') || '{}');
  } catch {}
  cache[query] = results;
  safeCacheStorage.setItem('searchResultsCache', JSON.stringify(cache));
}

// NEW: Interface for DragHandle component
interface DragHandleProps {
  item: any;
  disabled: boolean;
  onDragStart: (info: { item: any }) => void;
  isTapInterface?: boolean;
  children: React.ReactNode;
  isReadOnly?: boolean; // Add isReadOnly prop
}

// StableWrapper prevents flicker during drag operations
const StableWrapper = memo<StableWrapperProps>(({
  children,
  imdbID
}) => {
  // Create a stable key from ID, but don't use it in React rendering
  const stableKey = `stable-${imdbID}`;

  // Use CSS to prevent the element from changing during drag operations
  const style = {
    willChange: 'transform', // Hint to browser to use GPU
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden', // Safari support
    transform: 'translateZ(0)', // Force GPU acceleration
    WebkitTransform: 'translateZ(0)', // Safari support
    width: '100%',
    height: '100%',
    position: 'relative',
  } as CSSProperties; // Use CSSProperties for type safety

  return (
    <div style={style} data-imdb={imdbID}>
      {children}
    </div>
  );
}, (prev, next) => prev.imdbID === next.imdbID);

// Define props for StableWrapper
interface StableWrapperProps {
  children: React.ReactNode;
  imdbID: string;
}

// NEW COMPONENT: DragHandle - separates dnd-kit concerns from react-window
const DragHandle = memo(({
  item,
  disabled,
  onDragStart,
  isTapInterface = false,
  children,
  isReadOnly = false
}: Omit<DragHandleProps, 'onTapItem'>) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.imdbID,
    data: {
      item: item,
      type: 'poster'
    },
    disabled: disabled || isTapInterface || isReadOnly,
  });

  React.useEffect(() => {
    if (isDragging && onDragStart) {
      onDragStart({ item });
    }
  }, [isDragging, item, onDragStart]);

  return (
    <div
      ref={setNodeRef}
      {...(disabled || isTapInterface || isReadOnly ? {} : attributes)}
      {...(disabled || isTapInterface || isReadOnly ? {} : listeners)}
      style={{
        // Remove cursor logic - parent div will handle it
        // cursor: disabled || isReadOnly ? 'not-allowed' : (isTapInterface ? 'default' : 'grab'),
        width: '100%',
        height: '100%',
        // Ensure pointer events pass through if needed, although unnecessary if parent handles click
        // pointerEvents: isTapInterface ? 'none' : 'auto',
      }}
    >
      {children}
    </div>
  );
});

interface PosterDraggableProps {
  item: any;
  disabled: boolean;
  onDragStart?: (info: { item: any; tierIdx?: number; itemIdx?: number }) => void;
  cardSize?: CardSize;
  isTapInterface?: boolean;
  style?: React.CSSProperties; // Added style prop
  isLoaded: boolean; // NEW: Track loading state externally
  onImageLoadSuccess: (imdbID: string) => void; // NEW: Callback when image loads
  isReadOnly?: boolean; // Add isReadOnly prop
}

// NEW: Interface for PosterContent component
interface PosterContentProps {
  item: any;
  disabled: boolean;
  cardSize: CardSize;
  isTapInterface: boolean;
  isLoaded: boolean;
  onImageLoadSuccess: (imdbID: string) => void;
}

// Custom hook for handling image loading errors
const useImageErrorHandler = (posterUrl: string | undefined | null) => {
  const [imageError, setImageError] = useState(false);

  // Reset error state if posterUrl changes
  useEffect(() => {
    setImageError(false);
  }, [posterUrl]);

  const handleImageError = useCallback(() => {
    // console.warn(`[Image Error] Failed to load image: ${posterUrl}`);
    setImageError(true);
    if (posterUrl) {
      addBadPoster(posterUrl); // <-- ADD TO CACHE ON ERROR
    }
  }, [posterUrl]);

  return { imageError, handleImageError };
};

// Pure content component - no events, minimal dependencies
const PosterContent = memo(({
  item,
  disabled,
  cardSize = 'medium',
  isTapInterface = false,
  isLoaded,
  onImageLoadSuccess
}: PosterContentProps) => {
  const dims = CARD_DIMENSIONS[cardSize] || CARD_DIMENSIONS.medium;
  // Add a default item structure if item is null/undefined for safety
  const safeItem = item || { Poster: 'N/A', Title: 'Invalid Item' }; 

  // ---> Use the error handler hook <---
  const { imageError, handleImageError } = useImageErrorHandler(safeItem.Poster);
  const isKnownBad = isBadPoster(safeItem.Poster); // <-- CHECK CACHE

  // Determine if poster is valid and not in error state
  const hasValidPoster = !imageError && !isKnownBad && safeItem.Poster && safeItem.Poster !== 'N/A'; // <-- USE isKnownBad

  return (
    <div
      className={`bg-gray-800 rounded-lg shadow-md overflow-hidden relative border ${disabled ? 'border-red-600 opacity-60' : 'border-gray-700'}`}
      style={{
        width: `${dims.width}px`,
        height: `${dims.height}px`,
      }}
    >
      {hasValidPoster ? (
        <img
          src={safeItem.Poster}
          alt={safeItem.Title || 'Poster'}
          className="w-full h-full object-cover absolute top-0 left-0"
          // Fire success only when component is mounted and image loads
          onLoad={() => {
            // console.log(`[Image Load Success] Fired for: ${safeItem.Title}`);
            onImageLoadSuccess(safeItem.imdbID);
          }}
          onError={handleImageError} // ---> Add onError handler <---
          loading="lazy"
        />
      ) : (
        // Fallback display
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-600 text-gray-400 text-[10px] leading-tight text-center p-1 font-mono">
          <span className="line-clamp-3 break-words">{safeItem.Title || 'No Poster'}</span>
        </div>
      )}
      {/* Overlay Dimmer for Disabled Items */}
      {disabled && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <span className="text-white text-xs font-bold">In Tier</span>
        </div>
      )}
    </div>
  );
});

const PosterDraggable = memo(({
  item,
  disabled,
  onDragStart,
  cardSize = 'medium',
  isTapInterface = false,
  style, // Added style prop
  isLoaded, // Destructure new prop
  onImageLoadSuccess, // Destructure new prop
  isReadOnly = false // Add isReadOnly prop
}: PosterDraggableProps) => {
  // If disabled or tap interface is active, use a simpler div wrapper
  if (disabled || isTapInterface || isReadOnly) {
    return (
      <div
        className="draggable-poster-container"
        style={style}
      >
        <PosterContent
          item={item}
          disabled={disabled}
          cardSize={cardSize}
          isTapInterface={isTapInterface}
          isLoaded={isLoaded}
          onImageLoadSuccess={onImageLoadSuccess}
        />
      </div>
    );
  }
  
  // Otherwise, wrap with DragHandle for draggable items
  return (
    <div className="draggable-poster-container" style={style}>
      <DragHandle
        item={item}
        disabled={disabled}
        onDragStart={onDragStart as (info: { item: any }) => void}
        isTapInterface={isTapInterface}
        isReadOnly={isReadOnly}
      >
        <PosterContent
          item={item}
          disabled={disabled}
          cardSize={cardSize}
          isTapInterface={isTapInterface}
          isLoaded={isLoaded}
          onImageLoadSuccess={onImageLoadSuccess}
        />
      </DragHandle>
    </div>
  );
}, (prevProps, nextProps) => {
  // Compare all props EXCEPT style 
  return (
    prevProps.item?.imdbID === nextProps.item?.imdbID &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.cardSize === nextProps.cardSize &&
    prevProps.isTapInterface === nextProps.isTapInterface &&
    prevProps.isLoaded === nextProps.isLoaded &&
    prevProps.isReadOnly === nextProps.isReadOnly
  );
});

// Add FallbackGrid interface
interface FallbackGridProps {
  items: any[];
  cardSize: CardSize;
  isTapInterface: boolean;
  onTapItem?: (item: any, position: { x: number, y: number }) => void;
  tierItemIds: Set<string>;
  markImageAsLoaded: (imdbID: string) => void;
}

// Add a simple fallback grid that doesn't rely on react-window
const FallbackGrid = ({ items, cardSize, isTapInterface, onTapItem, tierItemIds, markImageAsLoaded }: FallbackGridProps) => {
  const { width: itemWidth, height: itemHeight, gap: itemGap } = CARD_DIMENSIONS[cardSize];
  
  // console.log('[FallbackGrid] Rendering fallback with', items.length, 'items');
  
  // ---> Create a mini image component with error handling for the fallback <---
  const FallbackImage = ({ item }: { item: any }) => {
    const { imageError, handleImageError } = useImageErrorHandler(item.Poster);
    const isKnownBad = isBadPoster(item.Poster); // <-- CHECK CACHE
    const hasValidPoster = !imageError && !isKnownBad && item.Poster && item.Poster !== 'N/A'; // <-- USE isKnownBad

    return (
      <>
        {hasValidPoster ? (
          <img 
            src={item.Poster} 
            alt={item.Title} 
            className="w-full h-full object-cover"
            onLoad={() => markImageAsLoaded(item.imdbID)}
            onError={handleImageError} // Use the error handler
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-700 text-gray-300 p-1 text-xs text-center">
            {item.Title || 'No Poster'}
          </div>
        )}
      </>
    );
  };
  // ---> End mini image component <---

  return (
    <div className="w-full flex flex-wrap gap-2 overflow-y-auto p-2" style={{ maxHeight: '100%' }}>
      {items.map(item => {
        if (!item) return null;
        const isDisabled = tierItemIds.has(item.imdbID);
        
        const handleTap = (e: React.MouseEvent<HTMLDivElement>) => {
          if (isTapInterface && onTapItem) {
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            onTapItem(item, { 
              x: rect.left + rect.width / 2, 
              y: rect.top + rect.height / 2 
            });
          }
        };
        
        return (
          <div 
            key={item.imdbID} 
            onClick={handleTap}
            className="rounded overflow-hidden relative"
            style={{ 
              width: itemWidth, 
              height: itemHeight,
              opacity: isDisabled ? 0.5 : 1
            }}
          >
            {/* Use the new FallbackImage component */}
            <FallbackImage item={item} />
            
            {isTapInterface && !isDisabled && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-blue-600 bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                  Tap to add
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ImdbSearch = memo(forwardRef(({ 
  searchResults, 
  setSearchResults, 
  tiers, 
  activeDrag, 
  setActiveDrag, 
  cardSize = 'medium',
  hideSearchInput = false,
  isTapInterface = false,
  onTapItem,
  useHorizontalLayout = false,
  isLoadingExternally = false, // Destructure with default
  listTitle,
  isReadOnly = false // Accept isReadOnly prop
}: ImdbSearchProps, ref) => {
  // State for container dimensions
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // State for query and loading
  const [query, setQuery] = React.useState(() => safeCacheStorage.getItem('searchQuery') || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Central state for tracking loaded image IDs
  const [loadedImages, setLoadedImages] = useState(() => new Set<string>());

  // Callback to mark an image as loaded
  const markImageAsLoaded = useCallback((imdbID: string) => {
    setLoadedImages(prev => new Set(prev).add(imdbID));
  }, []);

  // Memoize the set of IDs currently in tiers for efficient checking
  const tierItemIds = useMemo(() => {
    // console.log('[ImdbSearch] Recalculating tierItemIds Set...');
    return new Set(tiers.flatMap(tier => tier.items.map((item: any) => item.imdbID)));
  }, [tiers]);

  // Restore cached results for the query on mount
  React.useEffect(() => {
    if (query && !hideSearchInput) {
      const cached = getCachedResults(query);
      if (cached) setSearchResults(cached);
    }
  }, []);

  React.useEffect(() => {
    if (!hideSearchInput) {
      safeCacheStorage.setItem('searchQuery', query);
    }
  }, [query, hideSearchInput]);

  // Expose search method to parent via ref
  useImperativeHandle(ref, () => ({
    search: () => search(),
    // Add a new method to force remeasurement
    remeasureContainer: () => {
      // console.log('[ImdbSearch] Explicit remeasureContainerSize called');
      // Force multiple measurement attempts with increasing delays
      const attemptMeasure = (attempt = 1) => {
        const measured = measureContainer();
        if (!measured && attempt < 5) {
          // console.log(`[ImdbSearch] Measurement attempt ${attempt} failed, trying again in ${attempt * 50}ms`);
          setTimeout(() => attemptMeasure(attempt + 1), attempt * 50);
        } else if (measured) {
          // console.log(`[ImdbSearch] Measurement attempt ${attempt} succeeded`);
        } else {
          // console.log(`[ImdbSearch] All measurement attempts failed`);
        }
      };
      
      // Start the measurement attempts
      attemptMeasure();
      
      // Also trigger a resize event as a fallback mechanism
      window.dispatchEvent(new Event('resize'));
      
      return true;
    },
    // Add method to set query from parent component
    setQuery: (newQuery: string) => {
      setQuery(newQuery);
    }
  }));

  // Helper to check if a show is already in any tier
  const isInTiers = (imdbID: string) =>
    tiers && Array.isArray(tiers) && tiers.some((tier: any) => tier.items.some((i: any) => i.imdbID === imdbID));

  const search = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError('');
    // Check cache first
    const cached = getCachedResults(query);
    if (cached) {
      setSearchResults(cached);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`http://localhost:3001/api/omdb?s=${encodeURIComponent(query)}`);
      const data = await res.json();
      if (data.Response === 'True') {
        setSearchResults(data.Search);
        setCachedResults(query, data.Search);
      } else {
        setSearchResults([]);
        setCachedResults(query, []);
        setError(data.Error || 'No results found.');
      }
    } catch (e) {
      setError('Failed to fetch results.');
    }
    setLoading(false);
  };

  const posters = searchResults.filter(item => item.Poster && item.Poster !== 'N/A');

  // Function to attempt measurement and update state
  const measureContainer = () => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      // console.log('[ImdbSearch Measure Attempt] Measured:', width, height);
      // Only update if dimensions are valid and different from current state
      if (width > 0 && height > 0) {
        // Always update even if dimensions haven't changed - fix for mobile
        setContainerSize({ width, height });
        // console.log('[ImdbSearch Measure Success] State set to:', width, height);
        return true; // Indicate success
      }
    }
    // console.log('[ImdbSearch Measure Failed] containerRef.current:', containerRef.current);
    return false; // Indicate failure or no change needed
  };

  // Effect to set up ResizeObserver
  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        // Check if dimensions are valid before setting state
        if (width > 0 && height > 0) {
           setContainerSize({ width, height });
           // console.log('[ImdbSearch ResizeObserver Fired] New dimensions:', width, height);
        } else {
           // console.log('[ImdbSearch ResizeObserver Fired] Invalid dimensions received:', width, height);
        }
      }
    });

    let currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
      // console.log('[ImdbSearch ResizeObserver] Observer attached.');
    } else {
      console.warn('[ImdbSearch ResizeObserver] Could not attach observer, ref is null.');
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
        // console.log('[ImdbSearch ResizeObserver] Observer detached.');
      }
      observer.disconnect();
    };
  }, []); // Run only once on mount

  // Effect to attempt initial measurement after mount and on layout change
  useEffect(() => {
    // console.log('[ImdbSearch Initial Measure Effect] Running. Layout:', useHorizontalLayout ? 'Horizontal' : 'Grid');
    // Attempt initial measurement immediately
    const measured = measureContainer();

    // If initial measurement failed (e.g., element not ready), try again after a short delay
    if (!measured) {
        // console.log('[ImdbSearch Initial Measure Effect] Initial measure failed, setting timeout.');
        const timeoutId = setTimeout(() => {
            // console.log('[ImdbSearch Initial Measure Effect] Retrying measurement after timeout.');
            measureContainer();
        }, 100); // Delay of 100ms, adjust if needed
        return () => clearTimeout(timeoutId); // Cleanup timeout
    }
  }, [useHorizontalLayout]); // Rerun if layout changes

  const { width: containerWidth, height: containerHeight } = containerSize;
  const { width: cardWidth, height: cardHeight, gap: cardGap } = CARD_DIMENSIONS[cardSize];

  // Memoize itemData for react-window to prevent unnecessary cell re-renders
  const listOrGridItemData = useMemo(() => {
    // Calculate grid-specific values *if* needed inside memo
    const { width: itemWidth, height: itemHeight, gap: itemGap } = CARD_DIMENSIONS[cardSize];
    const columnCount = useHorizontalLayout ? 0 : Math.max(1, Math.floor((containerSize.width + itemGap) / (itemWidth + itemGap)));

    // console.log('[ImdbSearch] Recalculating listOrGridItemData');

    return {
      items: searchResults,
      columnCount, // Needed for GridCellRenderer logic
      cardSize,
      isTapInterface,
      onTapItem, // Callback from App
      setActiveDrag, // Callback from App
      itemWidth, // Needed for GridCellRenderer logic
      itemHeight, // Needed for GridCellRenderer logic
      tierItemIds, // Memoized Set
      loadedImages, // Add central loaded state
      markImageAsLoaded, // Add central callback
      isReadOnly // Pass isReadOnly
    };
  }, [
    searchResults,
    cardSize,
    isTapInterface,
    onTapItem,
    setActiveDrag,
    tierItemIds, // Add back: If tiers change, disabled state might change
    useHorizontalLayout, 
    containerSize.width, 
    containerSize.height,
    loadedImages, // Dependency on loaded state
    markImageAsLoaded, // Dependency on stable callback
    isReadOnly // Add isReadOnly dependency
  ]);

  // Filter posters *after* calculating itemData so renderers get all items
  // const posters = searchResults.filter(item => item.Poster && item.Poster !== 'N/A');

  // Update renderListItem to handle tap and cursor on the outer div
  const renderListItem = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const { items, cardSize, isTapInterface, onTapItem, setActiveDrag, tierItemIds, loadedImages, markImageAsLoaded, isReadOnly } = listOrGridItemData;
    const item = items[index];
    if (!item) return null;

    const isDisabled = tierItemIds.has(item.imdbID);
    const isLoaded = loadedImages.has(item.imdbID);
    const isTappable = isTapInterface && !isDisabled && !isReadOnly;

    // RE-ADD handleCellTap here
    const handleCellTap = (e: React.PointerEvent<HTMLDivElement>) => {
      if (isTappable && onTapItem) {
        // Check if it's a primary pointer action (avoid right-clicks, etc.)
        if (!e.isPrimary) return;
        
        e.preventDefault();
        e.stopPropagation(); // <-- ADD stopPropagation
        const rect = e.currentTarget.getBoundingClientRect();
        const position = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        onTapItem(item, position);
      }
    };

    // Add cursor style and accessibility props to the outer div based on tappability
    const cellStyle = {
      ...style,
      cursor: isTappable ? 'pointer' : (isDisabled || isReadOnly ? 'not-allowed' : 'default'),
    };

    return (
      <div 
        style={cellStyle} 
        onPointerUp={handleCellTap}
        role={isTappable ? "button" : undefined}
        tabIndex={isTappable ? 0 : -1}
        aria-label={item.Title || 'Movie/TV Poster'}
        aria-disabled={isDisabled || isReadOnly}
        className="list-cell-container"
      >
        <StableWrapper imdbID={item.imdbID}>
          <PosterDraggable
            item={item}
            disabled={isDisabled || isReadOnly}
            onDragStart={setActiveDrag}
            cardSize={cardSize}
            isTapInterface={isTapInterface}
            isLoaded={isLoaded}
            onImageLoadSuccess={markImageAsLoaded}
            isReadOnly={isReadOnly}
          />
        </StableWrapper>
      </div>
    );
  }, [listOrGridItemData]);

  // Update renderGridCell to handle tap and cursor on the outer div
  const renderGridCell = useCallback(({ columnIndex, rowIndex, style }: { columnIndex: number; rowIndex: number; style: React.CSSProperties }) => {
    const { items, columnCount, cardSize, isTapInterface, onTapItem, setActiveDrag, tierItemIds, itemWidth, itemHeight, loadedImages, markImageAsLoaded, isReadOnly } = listOrGridItemData;

    const index = rowIndex * columnCount + columnIndex;
    if (index >= items.length) {
        return null;
    }
    const item = items[index];
    if (!item) return null;

    const isDisabled = tierItemIds.has(item.imdbID);
    const isLoaded = loadedImages.has(item.imdbID);
    const isTappable = isTapInterface && !isDisabled && !isReadOnly;

    // RE-ADD handleCellTap here
    const handleCellTap = (e: React.PointerEvent<HTMLDivElement>) => {
      if (isTappable && onTapItem) {
        // Check if it's a primary pointer action (avoid right-clicks, etc.)
        if (!e.isPrimary) return;

        e.preventDefault();
        e.stopPropagation(); // <-- ADD stopPropagation
        const rect = e.currentTarget.getBoundingClientRect();
        const position = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        onTapItem(item, position);
      }
    };

    const itemGap = CARD_DIMENSIONS[cardSize]?.gap ?? 12;
    const styleWithGap = {
        ...style,
        left: `${parseFloat(style.left as string) + itemGap / 2}px`,
        top: `${parseFloat(style.top as string) + itemGap / 2}px`,
        width: `${itemWidth}px`,
        height: `${itemHeight}px`,
        cursor: isTappable ? 'pointer' : (isDisabled || isReadOnly ? 'not-allowed' : 'default'),
    };

    return (
      <div 
        style={styleWithGap} 
        onPointerUp={handleCellTap}
        role={isTappable ? "button" : undefined}
        tabIndex={isTappable ? 0 : -1}
        aria-label={item.Title || 'Movie/TV Poster'}
        aria-disabled={isDisabled || isReadOnly}
        className="grid-cell-container"
      >
        <StableWrapper imdbID={item.imdbID}>
          <PosterDraggable
            item={item}
            disabled={isDisabled || isReadOnly}
            onDragStart={setActiveDrag}
            cardSize={cardSize}
            isTapInterface={isTapInterface}
            isLoaded={isLoaded}
            onImageLoadSuccess={markImageAsLoaded}
            isReadOnly={isReadOnly}
          />
        </StableWrapper>
      </div>
    );
  }, [listOrGridItemData]);

  // Define itemKey functions using useCallback for stable reference
  const getItemKeyForList = useCallback((index: number) => listOrGridItemData.items[index]?.imdbID ?? index, [listOrGridItemData]);

  const getItemKeyForGrid = useCallback(({ columnIndex, rowIndex, data }: { columnIndex: number; rowIndex: number; data: any }) => {
    const index = rowIndex * data.columnCount + columnIndex;
    return data.items[index]?.imdbID ?? `${rowIndex}-${columnIndex}`;
  }, []); // No dependency on listOrGridItemData needed IF data is passed correctly by react-window

  // Show loading/error or the virtualized list/grid
  return (
    <div className="flex flex-col h-full" ref={containerRef}>
      {!hideSearchInput && (
        <div className="flex gap-2 mb-3 flex-shrink-0">
          {/* Search Input and Button - kept for completeness but not rendered by default */}
          <input
            className="border border-gray-600 rounded px-2 py-1 flex-1 text-sm bg-gray-700 text-white placeholder-gray-400"
            placeholder="Search movies or TV shows..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
          />
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm whitespace-nowrap search-button"
            onClick={search}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      )}

      {/* Error message */}
      {error && <div className="text-red-500 mb-2 text-sm flex-shrink-0">{error}</div>}

      {/* Results Grid/List */}
      <div className="flex-grow min-h-0"> {/* Allow grid/list to take remaining space */}
        {searchResults.length > 0 && (
          // If container has a valid size, use react-window
          containerSize.width > 0 ? (
            useHorizontalLayout ? (
              <FixedSizeList
                height={CARD_DIMENSIONS[cardSize].height + CARD_DIMENSIONS[cardSize].gap} // Height of one row + gap
                itemCount={searchResults.length}
                itemSize={CARD_DIMENSIONS[cardSize].width + CARD_DIMENSIONS[cardSize].gap} // Width of one item + gap
                layout="horizontal"
                width={containerSize.width}
                itemData={listOrGridItemData} // Use memoized data
                itemKey={getItemKeyForList} // Use memoized function
                style={{ 
                  overflowX: 'auto', 
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  touchAction: 'pan-x',
                }} // Enhanced touch scrolling
                className="horizontal-scroll-container"
              >
                {renderListItem}
              </FixedSizeList>
            ) :
              (() => { // IIFE to calculate grid dimensions
                const { width: itemWidth, height: itemHeight, gap: itemGap } = CARD_DIMENSIONS[cardSize];
                const columnCount = Math.max(1, Math.floor((containerSize.width + itemGap) / (itemWidth + itemGap)));
                
                // Log grid calculations (optional, for debugging)
                // console.log('[ImdbSearch Grid] Container W:', containerSize.width, 'Item W:', itemWidth, 'Gap:', itemGap, '-> Columns:', columnCount);
                // console.log('[ImdbSearch Grid] Container H:', containerSize.height, 'Item H:', itemHeight, 'Gap:', itemGap, '-> Rows:', Math.ceil(searchResults.length / columnCount));

                return (
                  <FixedSizeGrid
                    columnCount={columnCount}
                    columnWidth={itemWidth + itemGap}
                    height={containerSize.height}
                    rowCount={Math.ceil(searchResults.length / columnCount)}
                    rowHeight={itemHeight + itemGap}
                    width={containerSize.width}
                    itemData={listOrGridItemData} // Use memoized data
                    itemKey={getItemKeyForGrid} // Use memoized function
                  >
                    {renderGridCell}
                  </FixedSizeGrid>
                );
              })() // Immediately invoke the function
          ) : (
            // If measurements failed, use fallback grid
            <FallbackGrid
              items={searchResults}
              cardSize={cardSize}
              isTapInterface={isTapInterface}
              onTapItem={onTapItem}
              tierItemIds={tierItemIds}
              markImageAsLoaded={markImageAsLoaded}
            />
          )
        )}
        {/* Show loading state */}
        {(loading || isLoadingExternally) && searchResults.length === 0 && (
          <div className="text-center text-gray-400 mt-4">Loading results...</div>
        )}
        {/* Show no results message (only if not loading, no results, and no list title) */}
        {!loading && !isLoadingExternally && searchResults.length === 0 && query && !listTitle && (
           <div className="text-center text-gray-400 mt-4">No results found for "{query}".</div>
        )}
        {/* Show initial prompt (only if not loading and no query made) */}
        {!loading && !isLoadingExternally && searchResults.length === 0 && !query && (
          <div className="text-center text-gray-400 mt-4">Use the search bar or a starter grid to find items.</div>
        )}
      </div>
    </div>
  );
}));

export default ImdbSearch; 